import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SearchableBankSelect from '../components/SearchableBankSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { apiErrorMessage } from '../utils/requestTheme';
import { SearchableSelect } from '../components/SearchableSelect';
import { ImportDialog } from '../components/ImportDialog';
import { Send, LogOut, CheckCircle, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';

// Form Operasional Payment Request (OPR), tema merah maroon.
// Tipe Request, Perusahaan, dan Data Penerima diambil dari master kategori OPR
// (terpisah dari Internal). Perusahaan & Departemen dipilih user Operasional;
// Approval masih bisa mengganti perusahaan. Penerima dipilih dari Data Penerima
// OPR atau diketik manual — nama bank tetap dari daftar bank yang sama.

const EMPTY = {
  tipe_request: '', company_id: '', departemen_id: '', amount: '', recipient_id: '',
  penerima_nama: '', penerima_rekening: '', penerima_bank: '', penerima_wa: '', penerima_email: '', keterangan: '',
};
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function OperasionalPaymentForm() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestTypes, setRequestTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  // 'pilih' = dari Data Penerima OPR, 'manual' = diketik.
  const [penerimaMode, setPenerimaMode] = useState('pilih');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastRequestNumber, setLastRequestNumber] = useState('');
  const [formData, setFormData] = useState(EMPTY);
  const [tipeSearch, setTipeSearch] = useState('');
  const [showTipeDropdown, setShowTipeDropdown] = useState(false);
  // Kunci untuk me-reset SearchableBankSelect (state pencariannya internal).
  const [bankKey, setBankKey] = useState(0);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, d, r] = await Promise.all([
        api.get('/operasional/request-types'), api.get('/operasional/companies'),
        api.get('/departments'), api.get('/operasional/recipients'),
      ]);
      setRequestTypes(t.data || []);
      setCompanies(c.data || []);
      setDepartments(d.data || []);
      const rc = (r.data || []).map((x) => ({ ...x, label: `${x.nama} — ${x.nama_bank || '-'} ${x.nomor_rekening || ''}` }));
      setRecipients(rc);
      if (!rc.length) setPenerimaMode('manual');
    } catch (e) {
      toast.error('Gagal memuat data form');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTypes(); }, [fetchTypes]);

  const filteredTypes = useMemo(() => {
    if (!tipeSearch) return requestTypes;
    return requestTypes.filter(t => t.toLowerCase().includes(tipeSearch.toLowerCase()));
  }, [tipeSearch, requestTypes]);

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleTipeSelect = (type) => {
    set('tipe_request', type);
    setTipeSearch(type);
    setShowTipeDropdown(false);
  };

  const formatCurrency = (value) => value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const parseAmount = (formatted) => parseFloat(formatted.replace(/\./g, '')) || 0;

  const chosenRecipient = recipients.find((r) => r.id === formData.recipient_id);

  const resetForm = () => {
    setFormData(EMPTY);
    setPenerimaMode(recipients.length ? 'pilih' : 'manual');
    setTipeSearch('');
    setBankKey(k => k + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipe_request) return toast.error('Tipe Request wajib dipilih');
    if (!formData.company_id) return toast.error('Perusahaan wajib dipilih');
    if (!formData.departemen_id) return toast.error('Departemen wajib dipilih');
    if (parseAmount(formData.amount) <= 0) return toast.error('Amount wajib diisi');
    const base = {
      tipe_request: formData.tipe_request,
      company_id: formData.company_id,
      departemen_id: formData.departemen_id,
      amount: parseAmount(formData.amount),
      keterangan: formData.keterangan || '',
    };
    let body;
    if (penerimaMode === 'pilih') {
      if (!formData.recipient_id) return toast.error('Pilih penerima dari Data Penerima, atau pilih "Isi Manual"');
      body = { ...base, recipient_id: formData.recipient_id };
    } else {
      if (!formData.penerima_nama.trim()) return toast.error('Nama Penerima wajib diisi');
      if (!formData.penerima_rekening) return toast.error('Nomor Rekening wajib diisi');
      if (formData.penerima_rekening.length < 5) return toast.error('Nomor Rekening minimal 5 digit');
      if (!formData.penerima_bank) return toast.error('Nama Bank wajib dipilih dari daftar');
      if (formData.penerima_wa && !/^\+?[0-9]{8,15}$/.test(formData.penerima_wa)) return toast.error('No. WA hanya angka (boleh diawali +), 8-15 digit');
      if (formData.penerima_email && !EMAIL_RE.test(formData.penerima_email.trim())) return toast.error('Format email tidak valid');
      body = {
        ...base,
        penerima_nama: formData.penerima_nama.trim(),
        penerima_rekening: formData.penerima_rekening,
        penerima_bank: formData.penerima_bank,
        penerima_wa: formData.penerima_wa || null,
        penerima_email: formData.penerima_email.trim() || null,
      };
    }

    setSubmitting(true);
    try {
      const res = await api.post('/operasional/payment-requests', body);
      setLastRequestNumber(res.data.request_number);
      setShowSuccessDialog(true);
      toast.success('Operasional payment request berhasil diajukan!');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal mengajukan payment request'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#fbeaea] to-[#f3d6d6] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#800000]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fbeaea] to-[#f3d6d6] p-4 md:p-8" data-testid="operasional-payment-page">
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#800000]" style={{ fontFamily: 'Manrope, sans-serif' }}>HISA</h1>
            <p className="text-[#a52a2a] text-sm">Operasional Payment Request</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold">{user?.name}</p>
              <p className="text-xs text-[#800000]">Operasional User</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} className="border-[#d9a5a5] text-[#800000] hover:bg-[#fbeaea]" data-testid="header-logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Card className="max-w-2xl mx-auto shadow-xl border-[#e8bcbc]" data-testid="operasional-payment-form">
        <CardHeader className="bg-gradient-to-r from-[#800000] to-[#5c0000] text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Form Operasional Payment Request
          </CardTitle>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[#f3d6d6] text-sm">Isi form di bawah untuk mengajukan pembayaran operasional</p>
            <ImportDialog type="opr" title="Operasional Payment Request (OPR)" label="Import dari Excel"
              className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white" testId="import-opr" />
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Tipe Request — daftar sama dengan form Internal */}
            <div className="space-y-2">
              <Label className="font-semibold">Tipe Request <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="tipe-request-input"
                  placeholder="Cari atau pilih tipe request..."
                  value={tipeSearch}
                  onChange={(e) => { setTipeSearch(e.target.value); set('tipe_request', ''); setShowTipeDropdown(true); }}
                  onFocus={() => setShowTipeDropdown(true)}
                  className="pl-10"
                />
                {showTipeDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="tipe-request-dropdown">
                    {filteredTypes.length > 0 ? filteredTypes.map((type, i) => (
                      <button key={type} type="button" className="w-full text-left px-4 py-2.5 hover:bg-[#fbeaea] text-sm border-b border-slate-100 last:border-0" onClick={() => handleTipeSelect(type)} data-testid={`tipe-option-${i}`}>
                        {type}
                      </button>
                    )) : <p className="px-4 py-3 text-sm text-slate-500">Tidak ada hasil</p>}
                  </div>
                )}
              </div>
              {formData.tipe_request && <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {formData.tipe_request}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-semibold">Perusahaan <span className="text-red-500">*</span></Label>
                <SearchableSelect options={companies} value={formData.company_id} onChange={(v) => set('company_id', v)}
                  displayKey="nama_perusahaan" valueKey="id" placeholder="Ketik nama perusahaan..." testId="company-select" />
                {!companies.length && <p className="text-xs text-red-600">Belum ada perusahaan kategori OPR. Hubungi Purchasing/Admin.</p>}
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">Departemen <span className="text-red-500">*</span></Label>
                <SearchableSelect options={departments} value={formData.departemen_id} onChange={(v) => set('departemen_id', v)}
                  displayKey="nama" valueKey="id" placeholder="Ketik nama departemen..." testId="departemen-select" />
                {!departments.length && <p className="text-xs text-red-600">Belum ada departemen. Hubungi Admin.</p>}
              </div>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="font-semibold">Amount (Rp) <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">Rp</span>
                <Input data-testid="amount-input" placeholder="0" value={formData.amount} onChange={(e) => set('amount', formatCurrency(e.target.value))} className="pl-10 font-mono text-right" />
              </div>
            </div>

            {/* Penerima — manual */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-2">
                <h3 className="font-semibold text-slate-800">Data Penerima</h3>
                <div className="inline-flex rounded-md border bg-white p-0.5" data-testid="penerima-mode">
                  {[['pilih', 'Pilih dari Data Penerima'], ['manual', 'Isi Manual']].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setPenerimaMode(v)} disabled={v === 'pilih' && !recipients.length}
                      className={`px-3 py-1 text-xs rounded ${penerimaMode === v ? 'bg-[#800000] text-white' : 'text-slate-600 hover:bg-slate-100'} disabled:opacity-40`}
                      data-testid={`penerima-mode-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
              {penerimaMode === 'pilih' ? (
                <div className="space-y-3" data-testid="penerima-pilih">
                  <SearchableSelect options={recipients} value={formData.recipient_id} onChange={(v) => set('recipient_id', v)}
                    displayKey="label" valueKey="id" placeholder="Ketik nama penerima..." testId="recipient-select" />
                  {chosenRecipient && (
                    <div className="grid grid-cols-2 gap-2 text-sm bg-white rounded-md border p-3" data-testid="recipient-detail">
                      <div><p className="text-xs text-slate-500">Nama</p><p className="font-semibold">{chosenRecipient.nama}</p></div>
                      <div><p className="text-xs text-slate-500">No. Rekening</p><p className="font-mono">{chosenRecipient.nomor_rekening}</p></div>
                      <div><p className="text-xs text-slate-500">Bank</p><p>{chosenRecipient.nama_bank}</p></div>
                      <div><p className="text-xs text-slate-500">WA / Email</p><p>{chosenRecipient.nomor_wa || '-'} / {chosenRecipient.email || '-'}</p></div>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Penerima belum ada di daftar? Pilih <strong>Isi Manual</strong>.</p>
                </div>
              ) : (<>
              <div className="space-y-2">
                <Label className="font-semibold">Nama Penerima <span className="text-red-500">*</span></Label>
                <Input data-testid="penerima-nama" maxLength={100} value={formData.penerima_nama} onChange={(e) => set('penerima_nama', e.target.value.toUpperCase())} placeholder="NAMA SESUAI REKENING" className="bg-white uppercase" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">No. Rekening <span className="text-red-500">*</span></Label>
                  <Input data-testid="penerima-rekening" inputMode="numeric" maxLength={30} value={formData.penerima_rekening} onChange={(e) => set('penerima_rekening', e.target.value.replace(/\D/g, ''))} placeholder="Hanya angka" className="bg-white font-mono" />
                </div>
                <div className="bg-white rounded-md">
                  <SearchableBankSelect
                    key={bankKey}
                    id="opr-bank"
                    label="Nama Bank"
                    required
                    value={formData.penerima_bank}
                    onChange={(val) => set('penerima_bank', val)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">No. WA</Label>
                  <Input data-testid="penerima-wa" inputMode="tel" maxLength={16} value={formData.penerima_wa} onChange={(e) => set('penerima_wa', e.target.value.replace(/[^\d+]/g, ''))} placeholder="08xxxxxxxxxx" className="bg-white font-mono" />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Email</Label>
                  <Input data-testid="penerima-email" type="email" value={formData.penerima_email} onChange={(e) => set('penerima_email', e.target.value)} placeholder="nama@email.com" className="bg-white" />
                </div>
              </div>
              </>)}
            </div>

            {/* Keterangan */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Keterangan</Label>
              <textarea
                data-testid="keterangan-input"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-[#800000]"
                value={formData.keterangan}
                onChange={(e) => set('keterangan', e.target.value.toUpperCase())}
                placeholder="MASUKKAN KETERANGAN (OPSIONAL)..."
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="flex-1 bg-[#800000] hover:bg-[#660000] text-white py-6 text-lg font-semibold" data-testid="submit-payment-button">
                {submitting ? 'Mengajukan...' : <><Send className="mr-2 h-5 w-5" /> Ajukan Payment</>}
              </Button>
              <Button type="button" variant="outline" onClick={handleLogout} className="border-slate-300 py-6" data-testid="form-logout-button">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md" data-testid="success-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="h-6 w-6" /> Payment Request Berhasil!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#fbeaea] p-4 rounded-lg border border-[#e8bcbc] text-center">
              <p className="text-sm text-[#800000] mb-1">Nomor Request</p>
              <p className="font-mono font-bold text-xl text-[#800000]">{lastRequestNumber}</p>
            </div>
            <p className="text-center text-slate-600">Data dikirim ke Approval dengan status <strong>"Proses Bayar"</strong>.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => { setShowSuccessDialog(false); resetForm(); }} className="bg-[#800000] hover:bg-[#660000]" data-testid="add-another-button">
                <Plus className="mr-2 h-4 w-4" /> Tambah Lagi
              </Button>
              <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" /> Logout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showTipeDropdown && <div className="fixed inset-0 z-0" onClick={() => setShowTipeDropdown(false)} />}
    </div>
  );
}
